/* eslint-disable max-lines */
import { Link } from '@tanstack/react-router';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import DOMPurify from 'dompurify';
import { ExternalLinkIcon } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { TableHeader } from '@/components/DataTable/TableHeader';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { SearchResource } from '@/lib/queries/search';

const columnHelper = createColumnHelper<SearchResource>();

export const getColumns = ({
  searchTerm,
  from,
  to,
  showDeleted,
}: {
  searchTerm: string | undefined;
  from: string | undefined;
  to: string | undefined;
  showDeleted: boolean;
}): ColumnDef<SearchResource>[] => {
  const changesFilterEnabled = from !== undefined || to !== undefined;

  return [
    columnHelper.accessor(
      ({ resourceType, service }) => `${service} ${resourceType}`,
      {
        header: ({ column }) => (
          <TableHeader
            type="select"
            column={column}
            headersTranslationPath="search.table"
          />
        ),
        id: 'resourceType',
        size: 10,
      },
    ),
    columnHelper.accessor(({ resourceId }) => resourceId, {
      header: ({ column }) => (
        <TableHeader
          type="input"
          column={column}
          headersTranslationPath="search.table"
        />
      ),
      id: 'resourceId',
      cell: r => (
        <Link
          to="/resource/$resourceId"
          params={{ resourceId: r.row.original.hashedID }}
          className="underline break-all hover:text-blue-500"
          search={{ from, to }}
        >
          {r.getValue()}
        </Link>
      ),
      size: 10,
    }),
    columnHelper.accessor('account', {
      header: ({ column }) => (
        <TableHeader
          type="select"
          column={column}
          headersTranslationPath="search.table"
        />
      ),
      cell: ({
        row: {
          original: { account, accountHashedID },
        },
      }) =>
        account === undefined || accountHashedID === undefined ? (
          <FormattedMessage id="global.noValue" />
        ) : (
          <Link
            to="/resource/$resourceId"
            params={{ resourceId: accountHashedID }}
            className="underline hover:text-blue-500"
          >
            {account}
          </Link>
        ),
      id: 'account',
      size: 6,
    }),
    columnHelper.accessor('region', {
      header: ({ column }) => (
        <TableHeader
          type="select"
          column={column}
          headersTranslationPath="search.table"
        />
      ),
      cell: ({
        row: {
          original: { region, regionHashedID },
        },
      }) =>
        region === undefined || regionHashedID === undefined ? (
          <FormattedMessage id="global.noValue" />
        ) : (
          <Link
            to="/resource/$resourceId"
            params={{ resourceId: regionHashedID }}
            className="underline hover:text-blue-500"
          >
            {region}
          </Link>
        ),
      id: 'region',
      size: 7,
    }),
    columnHelper.accessor('service', {
      header: ({ column }) => (
        <TableHeader
          type="select"
          column={column}
          headersTranslationPath="search.table"
        />
      ),
      cell: ({
        row: {
          original: { service, serviceHashedID },
        },
      }) =>
        serviceHashedID === undefined ? (
          <FormattedMessage id="global.noValue" />
        ) : (
          <Link
            to="/resource/$resourceId"
            params={{ resourceId: serviceHashedID }}
            className="underline hover:text-blue-500"
          >
            {service}
          </Link>
        ),
      id: 'service',
      size: 7,
    }),
    columnHelper.accessor('matchedProperties', {
      header: () => (
        <FormattedMessage id="search.table.matchedProperties.header" />
      ),
      id: 'matchedProperties',
      cell: row => {
        const p = row.getValue();

        if (p === undefined) {
          return <></>;
        }

        return (
          <TooltipProvider>
            <div className="flex gap-2 max-w-[300px] overflow-hidden">
              {p.map(({ property, value }, i) => (
                <Tooltip delayDuration={100} key={i}>
                  <TooltipTrigger className="underline decoration-dotted">
                    {property}
                  </TooltipTrigger>
                  <TooltipContent>
                    <p
                      className="break-words"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          searchTerm !== undefined && searchTerm.length > 0
                            ? decodeURIComponent(value).replace(
                                searchTerm,
                                // text-red-500
                                `<span style="color: rgb(239 68 68 / var(--tw-text-opacity, 1))">${searchTerm}</span>`,
                              )
                            : (() => {
                                try {
                                  return decodeURIComponent(value);
                                } catch {
                                  return value;
                                }
                              })(),
                        ),
                      }}
                    />
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </TooltipProvider>
        );
      },
      size: 8,
    }),
    columnHelper.accessor(({ awsURL }) => awsURL, {
      header: () => <FormattedMessage id="search.table.awsUrl.header" />,
      id: 'awsUrl',
      cell: row =>
        row.getValue() !== '' ? (
          <a
            href={decodeURIComponent(row.getValue())}
            target="_blank"
            rel="noreferrer"
            className="underline hover:text-blue-500"
          >
            <FormattedMessage id="search.table.awsUrl.cell" />
            &nbsp;
            <ExternalLinkIcon className="h-4 inline" />
          </a>
        ) : (
          <FormattedMessage id="global.noValue" />
        ),
      size: 10,
    }),
    columnHelper.accessor(
      ({ codeGithubURL, moduleGithubURL }) => ({
        codeGithubURL,
        moduleGithubURL,
      }),
      {
        header: () => <FormattedMessage id="search.table.codeUrl.header" />,
        id: 'codeUrl',
        cell: row =>
          row.getValue().codeGithubURL === undefined &&
          row.getValue().moduleGithubURL === undefined ? (
            row.row.original.scannedInCloud ? (
              <FormattedMessage id="search.table.codeUrl.unmanaged" />
            ) : (
              <FormattedMessage id="global.noValue" />
            )
          ) : (
            <div>
              {row.getValue().codeGithubURL !== undefined ? (
                <a
                  href={row.getValue().codeGithubURL}
                  target="_blank"
                  rel="noreferrer"
                  className="underline inline-block hover:text-blue-500"
                >
                  <FormattedMessage id="search.table.codeUrl.resource" />
                  &nbsp;
                  <ExternalLinkIcon className="h-4 inline" />
                </a>
              ) : (
                <></>
              )}
              {row.getValue().moduleGithubURL !== undefined ? (
                <a
                  href={row.getValue().moduleGithubURL}
                  target="_blank"
                  rel="noreferrer"
                  className="underline inline-block hover:text-blue-500"
                >
                  <FormattedMessage id="search.table.codeUrl.module" />
                  &nbsp;
                  <ExternalLinkIcon className="h-4 inline" />
                </a>
              ) : (
                <></>
              )}
            </div>
          ),
        size: 10,
      },
    ),
    columnHelper.accessor(({ codeLastUpdatedAt }) => codeLastUpdatedAt, {
      header: ({ column }) => (
        <TableHeader
          type="sort-only"
          column={column}
          headersTranslationPath="search.table"
        />
      ),
      id: 'codeLastUpdatedAt',
      cell: row =>
        row.getValue() === undefined &&
        row.row.original.codeLastUpdatedURL === undefined ? (
          <FormattedMessage id="global.noValue" />
        ) : (
          <div>
            {row.row.original.codeLastUpdatedURL !== undefined ? (
              <a
                href={row.row.original.codeLastUpdatedURL}
                target="_blank"
                rel="noreferrer"
                className="underline inline-block hover:text-blue-500"
              >
                {row.getValue()}
                &nbsp;
                <ExternalLinkIcon className="h-4 inline" />
              </a>
            ) : (
              <></>
            )}
          </div>
        ),
      size: 10,
    }),
    columnHelper.accessor(({ updateCommitter }) => updateCommitter, {
      header: ({ column }) => (
        <TableHeader
          type="select"
          column={column}
          headersTranslationPath="search.table"
        />
      ),
      id: 'codeLastUpdatedBy',
      cell: row =>
        row.getValue() === undefined ? (
          <FormattedMessage id="global.noValue" />
        ) : (
          <div>{row.getValue()}</div>
        ),
      size: 10,
    }),
    ...(changesFilterEnabled
      ? [
          columnHelper.accessor('numberOfUpdates', {
            header: ({ column }) => (
              <TableHeader
                type="sort-only"
                headersTranslationPath="search.table"
                column={column}
              />
            ),
            size: 6,
          }),
        ]
      : []),
    ...(showDeleted && changesFilterEnabled
      ? [
          columnHelper.accessor(
            ({ deletedAt }) => (deletedAt !== undefined ? 'deleted' : 'active'),
            {
              header: ({ column }) => (
                <TableHeader
                  type="select"
                  headersTranslationPath="search.table"
                  optionsTranslationPath="search.table.deleted.options"
                  column={column}
                />
              ),
              id: 'deleted',
              size: 6,
              cell: row =>
                row.getValue() === 'deleted' ? '🔴 Deleted' : '🟢 Active',
            },
          ),
        ]
      : []),
  ] as ColumnDef<SearchResource>[];
};
